import React, { useState, useEffect } from 'react';
import { personalInfoAPI } from '../api/personalInfo.api';
import { toast } from 'react-hot-toast';

interface PersonalInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  addressStreet: string;
  addressNumber: string;
  addressCity: string;
  addressPostalCode: string;
}

export const InformationPersonnelleTab: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalData, setOriginalData] = useState<PersonalInfo | null>(null);

  const [formData, setFormData] = useState<PersonalInfo>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    addressStreet: '',
    addressNumber: '',
    addressCity: '',
    addressPostalCode: ''
  });

  useEffect(() => {
    // Load personal information from API
    const loadPersonalInfo = async () => {
      try {
        setIsLoading(true);
        const data = await personalInfoAPI.getPersonalInfo();

        const personalInfo: PersonalInfo = {
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          addressStreet: data.addressStreet || '',
          addressNumber: data.addressNumber || '',
          addressCity: data.addressCity || '',
          addressPostalCode: data.addressPostalCode || ''
        };

        setFormData(personalInfo);
        setOriginalData(personalInfo);
      } catch (error) {
        console.error('Error loading personal info:', error);
        toast.error('Erreur lors du chargement des informations');
      } finally {
        setIsLoading(false);
      }
    };

    loadPersonalInfo();
  }, []);

  const handleChange = (field: keyof PersonalInfo, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await personalInfoAPI.updatePersonalInfo({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        addressStreet: formData.addressStreet,
        addressNumber: formData.addressNumber,
        addressCity: formData.addressCity,
        addressPostalCode: formData.addressPostalCode
      });

      setOriginalData(formData);
      setHasUnsavedChanges(false);
      toast.success('Informations sauvegardées avec succès');
    } catch (error) {
      console.error('Error saving personal info:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (originalData) {
      setFormData(originalData);
    }
    setHasUnsavedChanges(false);
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px', flexDirection: 'column', gap: '16px' }}>
        <div className="mon-espace-spinner"></div>
        <p style={{ margin: 0, fontSize: '16px', color: '#6b7280' }}>Chargement de vos informations...</p>
      </div>
    );
  }

  return (
    <>
      <div className="mon-espace-actions" style={{ marginBottom: '24px' }}>
        {hasUnsavedChanges && (
          <button
            type="button"
            className="mon-espace-button cancel"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Annuler
          </button>
        )}
        <button
          type="button"
          className="mon-espace-button save"
          onClick={handleSave}
          disabled={isSaving || !hasUnsavedChanges}
        >
          {isSaving ? (
            <>
              <div className="mon-espace-button-spinner"></div>
              Sauvegarde...
            </>
          ) : (
            <>
              <svg
                className="mon-espace-button-icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Sauvegarder
            </>
          )}
        </button>
      </div>

      <div className="info-personnelle-form">
        <div className="info-section">
          <h3 className="info-section-title">Informations perso</h3>
          <div className="info-form-grid">
            <div className="info-form-group">
              <label className="info-label">Prénom</label>
              <input
                type="text"
                className="info-input"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                placeholder="Prénom"
              />
            </div>

            <div className="info-form-group">
              <label className="info-label">Nom</label>
              <input
                type="text"
                className="info-input"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                placeholder="Nom"
              />
            </div>
          </div>
        </div>

        <div className="info-section">
          <h3 className="info-section-title">Coordonnées</h3>
          <div className="info-form-grid">
            <div className="info-form-group">
              <label className="info-label">Email</label>
              <input
                type="email"
                className="info-input"
                value={formData.email}
                disabled
                title="L'adresse e-mail (identifiant de connexion) ne peut pas être modifiée ici."
                placeholder="email@example.com"
              />
            </div>

            <div className="info-form-group">
              <label className="info-label">Téléphone</label>
              <input
                type="tel"
                className="info-input"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+32 123 45 67 89"
              />
            </div>
          </div>
        </div>

        <div className="info-section">
          <h3 className="info-section-title">Adresse</h3>
          <div className="info-form-grid">
            <div className="info-form-group">
              <label className="info-label">Rue</label>
              <input
                type="text"
                className="info-input"
                value={formData.addressStreet}
                onChange={(e) => handleChange('addressStreet', e.target.value)}
                placeholder="Rue de la Loi"
              />
            </div>

            <div className="info-form-group">
              <label className="info-label">Numéro</label>
              <input
                type="text"
                className="info-input"
                value={formData.addressNumber}
                onChange={(e) => handleChange('addressNumber', e.target.value)}
                placeholder="123"
              />
            </div>
          </div>

          <div className="info-form-grid">
            <div className="info-form-group">
              <label className="info-label">Code postal</label>
              <input
                type="text"
                className="info-input"
                value={formData.addressPostalCode}
                onChange={(e) => handleChange('addressPostalCode', e.target.value)}
                placeholder="1000"
              />
            </div>

            <div className="info-form-group">
              <label className="info-label">Ville</label>
              <input
                type="text"
                className="info-input"
                value={formData.addressCity}
                onChange={(e) => handleChange('addressCity', e.target.value)}
                placeholder="Bruxelles"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
